import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import DeleteConfirmDialog from "@/components/ui/DeleteConfirmDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import EditIcon from "@/components/icons/EditIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import CloseIcon from "@/components/icons/CloseIcon";
import LocationIcon from "@/components/icons/LocationIcon";

const SitesIndexPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, site: null });
  const [isSearching, setIsSearching] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const { data, isLoading, error } = useQuery(
    ["sites", page, debouncedSearch, statusFilter],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
      });
      const response = await api.get(`/sites?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: "always",
    }
  );

  const deleteMutation = useMutation((siteId) => api.delete(`/sites/${siteId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["sites"]);
      queryClient.invalidateQueries(["mine-types"]);
      queryClient.refetchQueries(["mine-types"]);
      toast.success("Site deleted successfully");
      setDeleteDialog({ isOpen: false, site: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete site");
    },
  });

  const handleDeleteClick = (site) => {
    setDeleteDialog({
      isOpen: true,
      site: site,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.site) {
      deleteMutation.mutate(deleteDialog.site.id);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value === "all" ? "" : value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const formatLabel = (value) => {
    if (!value) return "-";
    const text = String(value).replace(/_/g, " ").toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600">Error loading sites: {error.message}</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="slide-up">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="order-2 sm:order-1">
          <Link to="/site/create" className="slide-up">
            <Button className="w-full gradient-primary hover:bg-red-900 sm:w-auto">
              <LocationIcon className="w-5 h-5 mr-2" />
              Add Site
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col order-1 w-full gap-3 sm:order-2 sm:w-auto sm:flex-row sm:items-end">
          <div className="w-full sm:w-64">
            <div className="relative">
              <Input
                placeholder="Search sites..."
                value={search}
                onChange={handleSearch}
                className="w-full pr-10 text-sm"
                aria-label="Search sites"
              />
              <Search
                className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2"
                aria-hidden="true"
              />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <Select value={statusFilter || ""} onValueChange={handleStatusFilter}>
              <SelectTrigger
                className="w-full focus:border-primary/40"
                aria-label="Filter by status"
              >
                <SelectValue placeholder="Filter by status">
                  {statusFilter === ""
                    ? "All Status"
                    : statusFilter === "operating"
                      ? "Operating"
                      : statusFilter === "care_maintenance"
                        ? "Care & Maintenance"
                        : statusFilter === "developing"
                          ? "Developing"
                          : "Filter by status"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-50 text-gray-400 rounded-b-lg shadow-lg bg-navy-100">
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="operating">Operating</SelectItem>
                <SelectItem value="care_maintenance">Care & Maintenance</SelectItem>
                <SelectItem value="developing">Developing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button onClick={clearFilters} className="w-full hover:bg-red-900 sm:w-auto">
              <CloseIcon className="w-5 h-5 mr-2" />
              <span className="whitespace-nowrap">Clear</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      {/* Table Wrapper for Horizontal Scroll on Mobile */}
      <div className="overflow-x-auto shadow-sm slide-up backdrop-blur-sm">
        <Table className="min-w-[700px] sm:min-w-full">
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-gray-50/50">
              <TableHead className="sticky whitespace-nowrap md:sticky md:left-0 md:top-0">Site Name</TableHead>
              <TableHead className="whitespace-nowrap">Client</TableHead>
              <TableHead className="whitespace-nowrap">Mine Type</TableHead>
              <TableHead className="text-center whitespace-nowrap">Status</TableHead>
              <TableHead className="text-center whitespace-nowrap">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isSearching ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center">
                  <div className="text-center">
                    <LoadingSpinner size="lg" />
                    <p className="mt-4 text-gray-600">Searching sites...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.data || data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center">
                  <div className="text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No sites found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((site) => (
                <TableRow key={site.id}>
                  <TableCell className="sticky max-w-80 md:sticky md:left-0 md:top-0">
                    <div className="p-4 my-2 mr-2 truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                      {site.name}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-80">
                    <div className="p-4 mx-2 mr-2 truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                      {site.client}
                    </div>
                  </TableCell>
                  <TableCell className="truncate max-w-40 whitespace-nowrap">
                    <div className="p-4 mx-2 mr-2 truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                      {site.mine_type || "N/A"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="p-4 mx-2 mr-2 text-center truncate rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                      <Badge
                        className="p-0 border-none"
                      >
                        {formatLabel(site.status || "Unknown")}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="w-4 text-right">
                    <div className="p-2 my-2 ml-2 text-center rounded-t-lg bg-navy-100 text-gray-0">
                      <div className="flex items-center justify-center gap-2">
                        <Link to={`/site/edit/${site.id}`}>
                          <Button variant="outline p-0" size="sm" className="rounded-lg">
                            <EditIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                          </Button>
                        </Link>
                        <Button
                          variant="outline p-0"
                          size="sm"
                          className="hover:text-red-600"
                          onClick={() => handleDeleteClick(site)}
                          disabled={site.usage_count > 0}
                        >
                          <DeleteIcon className="w-5 h-5 text-red-500 transition-transform duration-200 hover:scale-110" />
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col pr-0 border-t border-b slide-up border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
        <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
          <span className="block sm:inline">
            Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, data?.pagination?.total || 0)} of{" "}
            {data?.pagination?.total || 0} results
          </span>
        </div>
        <div className="flex items-center justify-center w-full sm:w-auto">
          <Button
            variant="outline"
            className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
            PREVIOUS
          </Button>
          <Button
            variant="outline"
            className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
          >
            {page}
          </Button>
          <Button
            variant="outline"
            className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= data?.pagination?.pages}
          >
            NEXT
            <ChevronRight className="w-4 h-4 ml-2 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, site: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Site"
        userName={deleteDialog.site?.name || ""}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default SitesIndexPage;
